import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { useState } from "react";

import { Step, StepLabel, Stepper } from "@mui/material";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { FormSelector } from "@/shared/selectors/FormSelector";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { useMutation } from "@tanstack/react-query";
import { createFormInstance } from "../repositories/FormsRepository";

const steps = ["Select Form", "Answer Questions"];

type Props = {
	show: boolean;
	onHide: (id?: number | null) => void;
};

export const CreateForm = ({ show, onHide }: Props) => {
	const [error, setError] = useState("");
	const [formId, setFormId] = useState<number | null>(null);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [activeStep, setActiveStep] = useState(0);

	const createForm = useMutation(
		() => {
			setIsSubmitLoading(true);
			return createFormInstance(formId);
		},
		{
			onSuccess: async (id: number) => {
				onHide(id);
			},
			onError: (error: Error) => {
				setActiveStep(0);
				setError(error.message);
			},
			onSettled: () => {
				setIsSubmitLoading(false);
				setFormId(null);
				setActiveStep(0);
			},
		},
	);

	const handleNext = () => {
		if (activeStep === 0) {
			createForm.mutate();
		}
		setActiveStep(activeStep + 1);
	};

	const handleBack = () => {
		setActiveStep(activeStep - 1);
	};

	return (
		<>
			<MesfModal
				maxWidth={"md"}
				open={show}
				handleClose={() => onHide()}
				title="NEW FORM INSTANCE"
				key={"new_form_instance"}
			>
				<form style={{ paddingTop: "15px" }}>
					<MesfModal.Content style={{ padding: "10px 24px" }} dividers>
						{activeStep !== steps.length - 1 ? (
							<Stepper
								activeStep={activeStep}
								alternativeLabel
								sx={{
									background: "none",
								}}
							>
								{steps.map((label) => (
									<Step key={label}>
										<StepLabel
											StepIconProps={{
												sx: {
													color: (theme) => theme.palette.primary.main,
													backgroundColor: (theme) =>
														theme.palette.background.paper,
													"&.Mui-completed": {
														color: (theme) => theme.palette.success.main,
													},
												},
											}}
										>
											{label}
										</StepLabel>
									</Step>
								))}
							</Stepper>
						) : (
							<></>
						)}
						{activeStep === 0 && (
							<Grid2 container justifyContent="center">
								<Grid2 size={{ xs: 12, md: 8 }}>
									<FormSelector
										value={formId}
										onChange={(id) => setFormId(id)}
									/>
								</Grid2>
							</Grid2>
						)}
						{activeStep === 1 && <CenteredLazyLoading />}
					</MesfModal.Content>
					<MesfModal.Actions style={{ margin: "15px 0", padding: "0 10px" }}>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ marginRight: "auto", padding: "8px 10px" }}
						>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide()}
							>
								Close
							</Button>
						</Grid2>
						<Grid2
							size={{ xs: 12, md: 3 }}
							style={{ margin: 0, padding: "8px 10px" }}
						>
							<Button
								disabled={(activeStep === 0 && !formId) || isSubmitLoading}
								variant="contained"
								color="primary"
								onClick={handleNext}
								fullWidth
							>
								Next
							</Button>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
